import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  Home,
  Brain,
  FolderOpen,
  Database,
  MessageSquare,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Shield,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Scale,
  BookOpen
} from 'lucide-react';
import LogoComponent from './Logo';
import { useAuth } from '../context/AuthContext';
import { AuthAPI } from '../services/api';

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
}

const SidebarContainer = styled.aside<{ $isCollapsed: boolean; $isMobile: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: ${({ $isCollapsed }) => $isCollapsed ? '80px' : '280px'};
  background: linear-gradient(180deg, 
    ${theme.colors.surface} 0%, 
    ${theme.colors.backgroundSecondary} 100%
  );
  border-right: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  transition: all ${theme.transitions.base};
  z-index: ${theme.zIndex.sticky};
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.md}) {
    width: ${({ $isMobile, $isCollapsed }) => $isMobile && !$isCollapsed ? '280px' : '0'};
    transform: ${({ $isMobile, $isCollapsed }) => 
      $isMobile && $isCollapsed ? 'translateX(-100%)' : 'translateX(0)'
    };
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: 100%;
    background: linear-gradient(180deg, 
      transparent 0%, 
      ${theme.colors.primary[200]} 20%, 
      ${theme.colors.primary[200]} 80%, 
      transparent 100%
    );
    opacity: 0.5;
  }
`;

const SidebarOverlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: ${theme.zIndex.overlay - 1};
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
  visibility: ${({ $isVisible }) => $isVisible ? 'visible' : 'hidden'};
  transition: all ${theme.transitions.base};

  @media (min-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const LogoSection = styled.div<{ $isCollapsed: boolean }>`
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: ${({ $isCollapsed }) => $isCollapsed ? 'center' : 'flex-start'};
  gap: ${theme.spacing[3]};
  position: relative;
  min-height: 80px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: ${theme.spacing[4]};
    right: ${theme.spacing[4]};
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      ${theme.colors.primary[300]} 20%, 
      ${theme.colors.primary[300]} 80%, 
      transparent 100%
    );
    opacity: 0.3;
  }
`;

const LogoLink = styled(Link)<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: all ${theme.transitions.base};
  transform-origin: center;

  &:hover {
    opacity: 0.8;
    transform: scale(1.02);
  }
`;

const LogoIconContainer = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $isCollapsed }) => $isCollapsed ? '60px' : '220px'};
  height: auto;
  transition: all ${theme.transitions.base};
`;

const CollapseButton = styled.button`
  position: absolute;
  top: 50%;
  right: ${theme.spacing[2]};
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: ${theme.borderRadius.full};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  z-index: 1;

  &:hover {
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.primary[700]};
    border-color: ${theme.colors.primary[300]};
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const Navigation = styled.nav`
  flex: 1;
  padding: ${theme.spacing[4]} 0;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary[300]};
    border-radius: ${theme.borderRadius.full};
  }
`;

const NavSection = styled.div`
  padding: 0 ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const NavSectionTitle = styled.div<{ $isCollapsed: boolean }>`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: ${theme.spacing[3]};
  padding: 0 ${theme.spacing[2]};
  opacity: ${({ $isCollapsed }) => $isCollapsed ? 0 : 1};
  transition: opacity ${theme.transitions.base};
  overflow: hidden;
`;

const NavDivider = styled.div<{ $isCollapsed: boolean }>`
  height: 1px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    ${theme.colors.primary[300]} 20%, 
    ${theme.colors.primary[300]} 80%, 
    transparent 100%
  );
  opacity: 0.3;
  margin: ${theme.spacing[4]} ${theme.spacing[4]};
  opacity: ${({ $isCollapsed }) => $isCollapsed ? 0 : 0.3};
  transition: opacity ${theme.transitions.base};
`;

const NavItem = styled(Link)<{ $isActive: boolean; $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[1]};
  border-radius: ${theme.borderRadius.lg};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ $isActive }) => 
    $isActive ? theme.colors.primary[700] : theme.colors.textSecondary};
  background: ${({ $isActive }) => 
    $isActive ? theme.colors.primary[100] : 'transparent'};
  border: 1px solid ${({ $isActive }) => 
    $isActive ? theme.colors.primary[300] : 'transparent'};
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  justify-content: ${({ $isCollapsed }) => $isCollapsed ? 'center' : 'flex-start'};

  &:hover {
    background: ${({ $isActive }) => 
      $isActive ? theme.colors.primary[200] : theme.colors.primary[100]};
    color: ${theme.colors.primary[700]};
    border-color: ${theme.colors.primary[300]};
    transform: translateX(2px);
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: ${({ $isActive }) => $isActive ? '60%' : '0'};
    background: ${theme.colors.primary[600]};
    border-radius: 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0;
    transition: all ${theme.transitions.base};
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const NavItemText = styled.span<{ $isCollapsed: boolean }>`
  opacity: ${({ $isCollapsed }) => $isCollapsed ? 0 : 1};
  transform: ${({ $isCollapsed }) => $isCollapsed ? 'translateX(-10px)' : 'translateX(0)'};
  transition: all ${theme.transitions.base};
  white-space: nowrap;
  overflow: hidden;
`;

const UserSection = styled.div`
  padding: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${theme.spacing[4]};
    right: ${theme.spacing[4]};
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      ${theme.colors.primary[300]} 20%, 
      ${theme.colors.primary[300]} 80%, 
      transparent 100%
    );
    opacity: 0.3;
  }
`;

const UserMenuContainer = styled.div`
  position: relative;
`;

const UserMenuButton = styled.button<{ $isOpen: boolean; $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[2]};
  background: ${({ $isOpen }) => $isOpen ? theme.colors.primary[100] : 'transparent'};
  border: 1px solid ${({ $isOpen }) => $isOpen ? theme.colors.primary[300] : theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.textPrimary};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  justify-content: ${({ $isCollapsed }) => $isCollapsed ? 'center' : 'space-between'};
  
  &:hover {
    background: ${theme.colors.primary[100]};
    border-color: ${theme.colors.primary[300]};
  }
`;

const UserInfo = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  opacity: ${({ $isCollapsed }) => $isCollapsed ? 0 : 1};
  transform: ${({ $isCollapsed }) => $isCollapsed ? 'translateX(-10px)' : 'translateX(0)'};
  transition: all ${theme.transitions.base};
  overflow: hidden;
`;

const UserAvatar = styled.div<{ $hasImage?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ $hasImage }) =>
    $hasImage
      ? 'transparent'
      : `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]})`};
  background-image: ${({ $hasImage }) => ($hasImage ? 'var(--avatar-url)' : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  flex-shrink: 0;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChevronIcon = styled(ChevronDown)<{ $isOpen: boolean; $isCollapsed: boolean }>`
  transition: transform ${theme.transitions.base};
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  color: ${theme.colors.textMuted};
  flex-shrink: 0;
  opacity: ${({ $isCollapsed }) => $isCollapsed ? 0 : 1};
`;

const UserDropdown = styled.div<{ $isOpen: boolean; $isCollapsed: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: ${({ $isCollapsed }) => $isCollapsed ? '100%' : '0'};
  margin-left: ${({ $isCollapsed }) => $isCollapsed ? '8px' : '0'};
  width: 220px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.95)'};
  transition: all ${theme.transitions.base};
  z-index: ${theme.zIndex.dropdown};
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
`;

const DropdownUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const DropdownUserDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownUserName = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[1]};
`;

const DropdownUserEmail = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownMenu = styled.div`
  padding: ${theme.spacing[2]};
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: none;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  text-align: left;
  
  &:hover {
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.primary[700]};
  }
  
  &.logout {
    color: ${theme.colors.error[600]};
    
    &:hover {
      background: ${theme.colors.error[100]};
      color: ${theme.colors.error[700]};
    }
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const MobileToggle = styled.button`
  display: none;
  position: fixed;
  top: ${theme.spacing[4]};
  left: ${theme.spacing[4]};
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  z-index: ${theme.zIndex.sticky + 1};
  box-shadow: ${theme.shadows.md};

  &:hover {
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.primary[700]};
    border-color: ${theme.colors.primary[300]};
  }

  @media (max-width: ${theme.breakpoints.md}) {
    display: flex;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Sidebar: React.FC<SidebarProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, dispatch, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Avatar-URL self-heal. AuthContext.initializeAuth seeds state.user from
  // /auth/me, which does NOT include avatar_url — that field lives in the
  // /auth/profile response. Without this effect, the avatar only appears on
  // routes that fetch the profile themselves (e.g. /profile). On every other
  // page load, the sidebar would show initials. This one-shot effect fixes
  // the gap: when we have an authenticated user but no avatar_url, fetch
  // the profile once and merge the URL into auth state.
  useEffect(() => {
    if (!state.user || state.user.avatar_url) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await AuthAPI.getUserProfile();
        if (cancelled) return;
        const url = resp?.success && resp.data?.avatar_url ? resp.data.avatar_url : null;
        if (url && state.user) {
          dispatch({ type: 'UPDATE_USER', payload: { ...state.user, avatar_url: url } });
        }
      } catch {
        // Silent failure — sidebar just falls back to initials, no UX impact.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-run whenever the user identity or avatar_url presence changes.
  }, [state.user?.id, state.user?.avatar_url, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Update CSS custom property for sidebar width
  useEffect(() => {
    const sidebarWidth = isCollapsed ? '80px' : '280px';
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
  }, [isCollapsed]);

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleProfileClick = () => {
    setIsUserMenuOpen(false);
    navigate('/profile');
  };

  const handleSettingsClick = () => {
    setIsUserMenuOpen(false);
    navigate('/settings');
  };

  const handleLogoutClick = async () => {
    setIsUserMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const handleAdminClick = () => {
    setIsUserMenuOpen(false);
    navigate('/admin');
  };

  // Get user data from auth context
  const currentUser = state.user ? (() => {
    const firstName = state.user.first_name?.trim() || '';
    const lastName = state.user.last_name?.trim() || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    
    const jobTitle = state.user.job_title?.trim() || '';
    const orgName = state.user.organization_name?.trim() || '';
    const professionalTitle = [jobTitle, orgName].filter(Boolean).join(' | ');
    
    const getInitials = () => {
      if (firstName && lastName) {
        return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
      } else if (fullName) {
        const names = fullName.split(' ');
        return names.length > 1 
          ? (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase()
          : fullName.substring(0, 2).toUpperCase();
      } else if (state.user) {
        return state.user.email.charAt(0).toUpperCase() + (state.user.email.split('@')[0]?.charAt(1)?.toUpperCase() || '');
      } else {
        return 'UN';
      }
    };
    
    return {
      name: fullName || state.user.email.split('@')[0] || 'User',
      email: state.user.email,
      role: professionalTitle || (state.user.is_admin ? 'Admin' : 'User'),
      initials: getInitials(),
      avatarUrl: state.user.avatar_url || '',
      isAdmin: state.user.is_admin
    };
  })() : {
    name: 'Unknown',
    email: 'unknown@example.com',
    role: 'User',
    initials: 'UN',
    avatarUrl: '',
    isAdmin: false
  };

  const mainNavigationItems = [
    { to: "/", icon: Home, label: "Home" },
    { to: "/tender-response", icon: Brain, label: "Tender Response" },
    { to: "/evaluation", icon: Scale, label: "Evaluation" },
    { to: "/projects", icon: FolderOpen, label: "Projects" },
    { to: "/tender-bank", icon: Database, label: "Tender Bank" },
    { to: "/knowledge-base", icon: BookOpen, label: "Knowledge Base" },
    { to: "/contact", icon: HelpCircle, label: "Support" },
  ];

  const accountNavigationItems = [
    { to: "/profile", icon: User, label: "Account" },
    { to: "/settings", icon: Settings, label: "Settings" },
  ];

  return (
    <>
      <MobileToggle onClick={handleMobileToggle}>
        {isMobileOpen ? <X /> : <Menu />}
      </MobileToggle>

      <SidebarOverlay 
        $isVisible={isMobileOpen} 
        onClick={() => setIsMobileOpen(false)} 
      />

      <SidebarContainer 
        $isCollapsed={isCollapsed} 
        $isMobile={!isMobileOpen}
      >
        <LogoSection $isCollapsed={isCollapsed}>
          <LogoLink to="/" $isCollapsed={isCollapsed}>
            <LogoIconContainer $isCollapsed={isCollapsed}>
              <LogoComponent size={isCollapsed ? 60 : 220} />
            </LogoIconContainer>
          </LogoLink>
          
          <CollapseButton onClick={handleToggleCollapse}>
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </CollapseButton>
        </LogoSection>

        <Navigation>
          <NavSection>
            <NavSectionTitle $isCollapsed={isCollapsed}>
              Main Navigation
            </NavSectionTitle>
            
            {mainNavigationItems.map(({ to, icon: Icon, label }) => {
              const isActive = to === "/" 
                ? location.pathname === "/" 
                : location.pathname.startsWith(to);
              
              return (
                <NavItem
                  key={to}
                  to={to}
                  $isActive={isActive}
                  $isCollapsed={isCollapsed}
                >
                  <Icon />
                  <NavItemText $isCollapsed={isCollapsed}>
                    {label}
                  </NavItemText>
                </NavItem>
              );
            })}
          </NavSection>

          <NavDivider $isCollapsed={isCollapsed} />

          <NavSection>
            <NavSectionTitle $isCollapsed={isCollapsed}>
              Account & Settings
            </NavSectionTitle>
            
            {accountNavigationItems.map(({ to, icon: Icon, label }) => {
              const isActive = location.pathname.startsWith(to);
              
              return (
                <NavItem
                  key={to}
                  to={to}
                  $isActive={isActive}
                  $isCollapsed={isCollapsed}
                >
                  <Icon />
                  <NavItemText $isCollapsed={isCollapsed}>
                    {label}
                  </NavItemText>
                </NavItem>
              );
            })}
          </NavSection>
        </Navigation>

        <UserSection>
          <UserMenuContainer ref={userMenuRef}>
            <UserMenuButton 
              $isOpen={isUserMenuOpen}
              $isCollapsed={isCollapsed}
              onClick={handleUserMenuToggle}
            >
              <UserInfo
                $isCollapsed={isCollapsed}
                role="link"
                aria-label="Open profile"
                title="Open profile"
                onClick={e => {
                  // Direct shortcut to /profile when the user clicks on the
                  // avatar / name / role region. The chevron beside it still
                  // toggles the menu (Settings / Logout / Admin) — best of
                  // both: one click to profile, dropdown for everything else.
                  e.stopPropagation();
                  handleProfileClick();
                }}
                style={{ cursor: 'pointer' }}
              >
                <UserAvatar
                  $hasImage={!!currentUser.avatarUrl}
                  style={
                    currentUser.avatarUrl
                      ? ({ '--avatar-url': `url(${currentUser.avatarUrl})` } as React.CSSProperties)
                      : undefined
                  }
                >
                  {!currentUser.avatarUrl && currentUser.initials}
                </UserAvatar>
                <UserDetails>
                  <UserName>{currentUser.name}</UserName>
                  <UserRole>{currentUser.role}</UserRole>
                </UserDetails>
              </UserInfo>
              <ChevronIcon $isOpen={isUserMenuOpen} $isCollapsed={isCollapsed} />
            </UserMenuButton>

            <UserDropdown $isOpen={isUserMenuOpen} $isCollapsed={isCollapsed}>
              <DropdownHeader>
                <DropdownUserInfo>
                  <UserAvatar
                    $hasImage={!!currentUser.avatarUrl}
                    style={
                      currentUser.avatarUrl
                        ? ({ '--avatar-url': `url(${currentUser.avatarUrl})` } as React.CSSProperties)
                        : undefined
                    }
                  >
                    {!currentUser.avatarUrl && currentUser.initials}
                  </UserAvatar>
                  <DropdownUserDetails>
                    <DropdownUserName>{currentUser.name}</DropdownUserName>
                    <DropdownUserEmail>{currentUser.email}</DropdownUserEmail>
                  </DropdownUserDetails>
                </DropdownUserInfo>
              </DropdownHeader>
              
              <DropdownMenu>
                <DropdownItem onClick={handleProfileClick}>
                  <User />
                  Profile
                </DropdownItem>

                <DropdownItem onClick={handleSettingsClick}>
                  <Settings />
                  Settings
                </DropdownItem>

                {currentUser.isAdmin && (
                  <DropdownItem onClick={handleAdminClick}>
                    <Shield />
                    Admin Panel
                  </DropdownItem>
                )}

                <DropdownItem className="logout" onClick={handleLogoutClick}>
                  <LogOut />
                  Logout
                </DropdownItem>
              </DropdownMenu>
            </UserDropdown>
          </UserMenuContainer>
        </UserSection>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;